import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { apiService } from '../../services/apiService';
import type { User } from '../../types';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { logger } from '../../utils/logger';

interface LoginProps {
  onLogin: (
    token: string,
    user: User,
    rememberMe: boolean,
    expiresIn: string,
    refreshExpiresIn?: string
  ) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { t } = useTranslation(['common', 'auth']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ensure API config (backend URL etc.) is initialized before login attempts
  useEffect(() => {
    const initApi = async () => {
      try {
        const { apiConfig } = await import('../../config/api');
        await apiConfig.initialize();
      } catch {
        // Config init failure will surface when user submits login
      }
    };
    initApi();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Use apiService to automatically handle network configuration
      const response = await apiService.post<{
        token: string;
        user: User;
        expiresIn: string;
        refreshExpiresIn?: string;
      }>('/auth/login', {
        email,
        password,
        rememberMe,
      });
      const data = response.data;
      onLogin(
        data.token,
        data.user,
        rememberMe,
        data.expiresIn,
        data.refreshExpiresIn
      );
    } catch (err: unknown) {
      logger.error('Login error:', err);
      setError(err instanceof Error ? err.message : t('auth.networkError', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      sx={{ p: { xs: 2, sm: 3 } }}
    >
      <Paper
        sx={{
          p: { xs: 3, sm: 4 },
          minWidth: { xs: '100%', sm: 320 },
          maxWidth: { xs: '100%', sm: 400 },
          width: '100%',
        }}
      >
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <LanguageSwitcher />
        </Box>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            textAlign: 'center',
            mb: { xs: 2, sm: 1 },
          }}
        >
          {t('login.title', { ns: 'auth' })}
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label={t('auth.email', { ns: 'common' })}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '1rem', sm: '0.875rem' },
                py: { xs: 1.5, sm: 1 },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '1rem', sm: '0.875rem' },
              },
            }}
          />
          <TextField
            label={t('auth.password', { ns: 'common' })}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '1rem', sm: '0.875rem' },
                py: { xs: 1.5, sm: 1 },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '1rem', sm: '0.875rem' },
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                color="primary"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: 24, sm: 20 },
                  },
                }}
              />
            }
            label={t('auth.rememberMe', { ns: 'common' })}
            sx={{
              mt: { xs: 2, sm: 1 },
              '& .MuiTypography-root': {
                fontSize: { xs: '1rem', sm: '0.875rem' },
              },
            }}
          />
          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                '& .MuiAlert-message': {
                  fontSize: { xs: '0.9rem', sm: '0.875rem' },
                },
              }}
            >
              {error}
            </Alert>
          )}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              fullWidth
              size="large"
              sx={{
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '1.1rem', sm: '0.875rem' },
                fontWeight: 'bold',
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.signIn', { ns: 'common' })}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default Login;
